"use client";

import { useEffect } from "react";
import ReportingService from "src/services/ReportingService";
import { fireDynamicComponentNotDisplayedEvent } from "lib/helper/analytics/fire-dynamic-component-not-displayed";
import { runAnalyticsTask } from "lib/service/analytics-defer/analytics-defer";
import { deleteCookie } from "src/utils/deleteCookie";
import { ImpressionsData } from "lib/helper/build-impressions-data";
import {
  loadCookies,
  postPageLoadEffects,
  suppressAutoPageLoad,
  trackImpressions
} from "app/components/client-bootstrap/client-helper";

/**
 * v3 client bootstrap — page view, component impressions and the deferred GTM
 * trigger. A v3-only copy of PageBootstrapClient rather than a reuse of it:
 * that component pulls in the cart, and its DDS analytics-service import
 * reaches @mui/material via dds.utils -> dtv-theme, which v3 must not ship.
 *
 * Deliberately absent: cart_summary (enableCartSummary / cartSvodsData).
 * Attribution cookies marked "write" are set server-side in
 * server/handlers/app-router-handler.ts; loadCookies skips those.
 */
interface IV3BootstrapClientProps {
  reqQueryParams: Record<string, string>;
  evolvUUID?: string;
  /** The v2-shaped `sb/<page>` key, so the GA page name matches v2. */
  page?: string;
  pagePath?: string;
  /** WAT-10651: analytics names of personalized components, extracted server-side. */
  personalizedComponentNames?: string[];
  /** WAT-10899: personalized slots that resolved to no component. */
  typelessPlaceholderNames?: string[];
  /** WAT-10897: sendImpressions payload pre-computed server-side. */
  impressionsData?: ImpressionsData;
  /** Page cookie entries (agentId / utCode / oem / rpid), built server-side as on v2. */
  cookieList?: any[];
}

const V3BootstrapClient = (props: IV3BootstrapClientProps) => {
  const {
    page,
    pagePath,
    reqQueryParams,
    evolvUUID,
    personalizedComponentNames,
    typelessPlaceholderNames,
    impressionsData,
    cookieList
  } = props;

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];

    // WAT-10897: suppress DMI's auto page-load at mount, before the deferred
    // trackImpressions below — see suppressAutoPageLoad() for the rationale.
    suppressAutoPageLoad();

    loadCookies({
      reqQueryParams,
      cookieList
    });

    const trackImpressionsArgs = {
      reqQueryParams,
      page,
      pagePath,
      root: "",
      // WAT-10897: feed sendImpressions the server-computed payload so it doesn't early-return
      ...impressionsData
    };

    // Defer 3rd-party tags until first user interaction OR 3s fallback.
    // Matches PageBootstrapClient and the Page Router, to protect INP.
    let thirdPartyLoaded = false;
    let thirdPartyTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
    // WAT-11358: ids for the deferred analytics callback below, so the cleanup
    // can cancel it — a soft navigation inside the idle window would otherwise
    // let it run against the unmounted page's props.
    let analyticsIdleId: any;
    let analyticsTimeoutId: ReturnType<typeof setTimeout> | undefined = undefined;
    const interactionEvents = ["click", "scroll", "keydown", "touchstart"];
    const loadThirdPartyTags = () => {
      if (thirdPartyLoaded) {
        return;
      }
      thirdPartyLoaded = true;
      if (thirdPartyTimeout) {
        clearTimeout(thirdPartyTimeout);
      }
      interactionEvents.forEach(evt => window.removeEventListener(evt, loadThirdPartyTags));
      window.dataLayer = window.dataLayer || [];
      // Rides the shared post-paint analytics queue — this fires inside the
      // first-interaction listener, i.e. in the INP-critical frame.
      runAnalyticsTask(() => window.dataLayer.push({ event: "marTechTrigger" }));
    };

    const windowLoadCallBack = () => {
      const fireAnalytics = () => {
        // WAT-10651: the `displayed` half of `dynamic_component`. Fired through
        // the app's own ReportingService, not the DDS analytics service — that
        // package pulls @directv-digital/dds.utils → dtv-theme → @mui/material.
        // Both push the identical four-key payload.
        if (personalizedComponentNames && personalizedComponentNames.length > 0) {
          const service = new ReportingService();
          personalizedComponentNames.forEach(component => {
            if (component) {
              service.fireModifiedComponentTypePageLoadEvent(component);
            }
          });
        }

        // WAT-10899: the `not displayed` half, fired from the same deferred
        // callback so both cohorts land together.
        typelessPlaceholderNames?.forEach(name => {
          fireDynamicComponentNotDisplayedEvent(name);
        });
      };

      if ("requestIdleCallback" in window) {
        analyticsIdleId = requestIdleCallback(
          () => {
            fireAnalytics();
          },
          { timeout: 2000 }
        );
      } else {
        analyticsTimeoutId = setTimeout(() => {
          fireAnalytics();
        }, 0);
      }

      // First-party impressions self-idle via requestIdleCallback inside trackImpressions.
      trackImpressions(trackImpressionsArgs);
    };

    // Registered OUTSIDE windowLoadCallBack so interactions between hydration
    // and window.load still trigger the defer, and the 3s fallback is measured
    // from effect mount.
    interactionEvents.forEach(evt => window.addEventListener(evt, loadThirdPartyTags, {
      once: true,
      passive: true
    }));
    thirdPartyTimeout = setTimeout(loadThirdPartyTags, 3000);

    if (document.readyState === "complete") {
      windowLoadCallBack();
    } else {
      window?.addEventListener("load", windowLoadCallBack);
    }

    postPageLoadEffects({
      evolvUUID
    });

    /**
     * Delete the shop team's "intent" cookie unless the caller opts out.
     * @see {@link https://directv-it.atlassian.net/browse/WAT-8043}
     */
    const params = new URLSearchParams(window?.location?.search);
    if (params?.get("retainCookie") !== "true") {
      deleteCookie("intent");
    }

    return () => {
      window.removeEventListener("load", windowLoadCallBack);
      if (thirdPartyTimeout) {
        clearTimeout(thirdPartyTimeout);
      }
      // WAT-11358: cancel the deferred analytics callback — see the ids above.
      if (analyticsIdleId && "cancelIdleCallback" in window) {
        cancelIdleCallback(analyticsIdleId);
      }
      if (analyticsTimeoutId) {
        clearTimeout(analyticsTimeoutId);
      }
      interactionEvents.forEach(evt => window.removeEventListener(evt, loadThirdPartyTags));
    };
  }, []);

  // UF navigation history in session storage, for the shop team (WAT-5710).
  useEffect(() => {
    try {
      const currentPage = window.location.href;
      const history = JSON.parse(
        sessionStorage.getItem("navigationHistory") || "[]"
      );
      if (
        history?.[history?.length - 1] !== currentPage &&
        !props?.page?.includes?.("/modal")
      ) {
        history.push(currentPage);
      }
      sessionStorage.setItem("navigationHistory", JSON.stringify(history || []));
    } catch (error) {
      console.error("Error updating navigation history:", error);
    }
  }, [props?.page]);

  return null;
};

export default V3BootstrapClient;
